import type { ReactNode } from 'react';
import { cn } from 'src/lib/utils';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
} from 'recharts';
import { ApproximateMetricMarker } from '../approximate-metric-marker';
import { BetaMetricCardSkeleton } from './beta-chart-skeletons';
import { BETA_METRIC_VARIANT_STYLES, type BetaMetricVariant } from './beta-metric-styles';

export type { BetaMetricVariant } from './beta-metric-styles';
export { BETA_METRIC_VARIANT_STYLES } from './beta-metric-styles';

const VARIANT_STYLES = BETA_METRIC_VARIANT_STYLES;

interface BetaMetricCardProps {
  label: string;
  value: string;
  indicatorLabel?: string;
  changeLabel?: string;
  changeDirection?: 'up' | 'down' | 'neutral';
  variant: BetaMetricVariant;
  sparklineData?: Array<{ value: number }>;
  className?: string;
  isLoading?: boolean;
  isApproximate?: boolean;
}

const CHANGE_ARROW = {
  up: '↑',
  down: '↓',
  neutral: '→',
} as const;

export function BetaMetricCard({
  label,
  value,
  indicatorLabel,
  changeLabel,
  changeDirection = 'neutral',
  variant,
  sparklineData = [],
  className,
  isLoading = false,
  isApproximate = false,
}: BetaMetricCardProps) {
  if (isLoading) {
    return <BetaMetricCardSkeleton variant={variant} className={className} />;
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl p-5 text-white shadow-[0_4px_20px_rgba(13,15,26,0.10)] transition-transform hover:-translate-y-0.5',
        VARIANT_STYLES[variant],
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-6 -top-6 h-[90px] w-[90px] rounded-full bg-white/10"
      />
      <div className="mb-2 flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.5px] opacity-75">
            {label}
          </div>
          {indicatorLabel ? (
            <div className="mt-1 text-[11px] font-medium normal-case tracking-normal opacity-90">
              {indicatorLabel}
            </div>
          ) : null}
        </div>
        {changeLabel ? (
          <div className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-1 text-[11px] font-bold">
            {CHANGE_ARROW[changeDirection]} {changeLabel}
          </div>
        ) : null}
      </div>
      <div className="mb-2 text-[26px] font-bold leading-none tracking-[-0.8px]">
        {isApproximate ? (
          <ApproximateMetricMarker variant="beta">{value}</ApproximateMetricMarker>
        ) : (
          value
        )}
      </div>
      {sparklineData.length > 1 ? (
        <div className="h-9 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData}>
              <Area
                type="monotone"
                dataKey="value"
                stroke="rgba(255,255,255,0.9)"
                fill="rgba(255,255,255,0.18)"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-9" />
      )}
    </div>
  );
}

export function BetaMetricCardRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {children}
    </div>
  );
}
